import { useRef } from 'react';
import { Music, Play, Check, Loader2, Volume2 } from 'lucide-react';
import PageHeader from '../../components/settings/PageHeader';
import SettingCard from '../../components/settings/SettingCard';
import Toggle from '../../components/settings/Toggle';
import { settingsService } from '../../services/settings/settings.service';
import { useSettingsStore } from '../../store/settingsStore';
import { cn } from '../../utils/helpers';

// Distinct preview tones per sound id, synthesized with the Web Audio API so no
// audio assets are needed. A real app would play bundled sound files instead.
const TONES: Record<string, number[]> = {
  chime: [660, 880],
  pulse: [520, 520],
  ripple: [440, 587],
  beacon: [784, 988],
  none: [],
};

export default function NotificationSounds() {
  const { data, saveNotifications, saving } = useSettingsStore();
  const selected = data?.notifications.sound ?? 'chime';
  const vibration = data?.notifications.vibration ?? true;
  const sounds = settingsService.getNotificationSounds();
  const audioCtxRef = useRef<AudioContext | null>(null);

  const preview = (id: string) => {
    const freqs = TONES[id];
    if (!freqs || freqs.length === 0) return;
    try {
      audioCtxRef.current ??= new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      const ctx = audioCtxRef.current;
      freqs.forEach((f, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = f;
        const start = ctx.currentTime + i * 0.16;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.25, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.3);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(start);
        osc.stop(start + 0.32);
      });
    } catch {
      /* audio not available */
    }
  };

  return (
    <div className="space-y-6 max-w-xl">
      <PageHeader title="Notification sound" subtitle="Tap a tone to preview, then select it" icon={Music} backTo="/app/settings/notifications" />

      <SettingCard flush>
        {sounds.map((sound) => {
          const active = selected === sound.id;
          return (
            <div key={sound.id} className={cn('flex items-center gap-3 px-5 py-4 border-b border-neutral-100 dark:border-neutral-800 last:border-0', active && 'bg-neutral-50 dark:bg-neutral-950')}>
              {sound.id !== 'none' ? (
                <button onClick={() => preview(sound.id)} aria-label={`Preview ${sound.name}`} className="w-10 h-10 rounded-2xl bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-900 hover:text-white text-neutral-600 dark:text-neutral-300 flex items-center justify-center transition-colors shrink-0">
                  <Play className="w-4 h-4" />
                </button>
              ) : (
                <div className="w-10 h-10 rounded-2xl bg-neutral-100 dark:bg-neutral-800 text-neutral-400 dark:text-neutral-500 flex items-center justify-center shrink-0">
                  <Volume2 className="w-4 h-4" />
                </div>
              )}
              <button onClick={() => saveNotifications({ sound: sound.id })} className="flex-1 text-left min-w-0">
                <p className="font-semibold text-sm text-neutral-900 dark:text-neutral-50">{sound.name}</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{sound.description}</p>
              </button>
              {active && (saving ? <Loader2 className="w-5 h-5 animate-spin text-neutral-400 dark:text-neutral-500" /> : <Check className="w-5 h-5 text-neutral-900 dark:text-neutral-50" />)}
            </div>
          );
        })}
      </SettingCard>

      <SettingCard flush>
        <div className="flex items-center justify-between gap-4 px-5 py-4">
          <div><p className="font-semibold text-sm">Vibration</p><p className="text-xs text-neutral-500 dark:text-neutral-400">Vibrate alongside the sound</p></div>
          <Toggle checked={vibration} onChange={(v) => saveNotifications({ vibration: v })} label="Vibration" />
        </div>
      </SettingCard>
    </div>
  );
}
